/* Multi-Step Approval Chain — Timeline UI component.
 *
 * Renders the chain as a vertical timeline next to the quotation
 * detail panel. Each step shows:
 *   • status icon (pending → active → approved → rejected)
 *   • role chips (e.g. "Sales Manager · Finance Manager")
 *   • mode chip for AND / OR steps
 *   • approver name + decision note + snapshot hash chip
 *
 * Props
 *   chain   — the chain document returned by /api/quotations/{id}/approval-chain
 *   onDecide(decision, note) — async callback invoked when the current
 *                              user decides on the active step
 *   currentUserRole          — string ("admin", "accountant", ...)
 *   currentUserId            — string
 */
import React, { useState } from 'react';
import { CheckCircle2, XCircle, Clock, Hourglass, Lock, ShieldCheck } from 'lucide-react';


const STEP_TONE = {
  approved: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  rejected: 'bg-rose-50 border-rose-300 text-rose-900',
  pending:  'bg-amber-50 border-amber-300 text-amber-900',
  inactive: 'bg-gray-50 border-gray-200 text-gray-500',
};

const StepIcon = ({ status, isCurrent }) => {
  if (status === 'approved') return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
  if (status === 'rejected') return <XCircle className="w-5 h-5 text-rose-600" />;
  if (isCurrent) return <Hourglass className="w-5 h-5 text-amber-600 animate-pulse" />;
  return <Clock className="w-5 h-5 text-gray-400" />;
};


const ApprovalChainTimeline = ({ chain, onDecide, currentUserRole, currentUserId }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!chain) return null;

  const currentIdx = chain.current_step_index ?? 0;
  const steps = chain.steps || [];

  const canActOnCurrent = (() => {
    if (chain.status !== 'in_progress') return false;
    const step = steps[currentIdx];
    if (!step) return false;
    if (currentUserRole === 'admin') return true;
    return (step.roles || []).includes(currentUserRole);
  })();

  // Submitter cannot self-approve (except admin override).
  const isSubmitter = chain.requested_by === currentUserId;
  const canDecide   = canActOnCurrent && (!isSubmitter || currentUserRole === 'admin');

  const submit = async (decision) => {
    setBusy(true);
    try { await onDecide(decision, note); setNote(''); }
    finally { setBusy(false); }
  };

  const statusChip = chain.status === 'approved' ? (
    <span data-testid="chain-status-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs">
      <ShieldCheck className="w-3 h-3" /> Approved
    </span>
  ) : chain.status === 'rejected' ? (
    <span data-testid="chain-status-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200 text-xs">
      <XCircle className="w-3 h-3" /> Rejected
    </span>
  ) : chain.status === 'superseded' ? (
    <span data-testid="chain-status-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200 text-xs">
      <Lock className="w-3 h-3" /> Superseded
    </span>
  ) : (
    <span data-testid="chain-status-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 text-xs">
      <Hourglass className="w-3 h-3" /> In Progress · Step {currentIdx + 1} of {steps.length}
    </span>
  );

  return (
    <div data-testid="approval-chain-timeline" className="mb-4 border border-indigo-200 bg-white rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-200 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-indigo-900 font-semibold text-sm">
          <ShieldCheck className="w-4 h-4" />
          Multi-Step Approval Chain
          {chain.tier_label && (
            <span className="ml-1 text-xs px-2 py-0.5 rounded bg-white border border-indigo-200 text-indigo-700 font-normal">
              {chain.tier_label}
            </span>
          )}
        </div>
        {statusChip}
      </div>

      <ol className="relative pl-8 pr-3 py-3 space-y-3">
        {steps.map((step, i) => {
          const isCurrent = i === currentIdx && chain.status === 'in_progress';
          const tone = STEP_TONE[step.status] || (i < currentIdx ? STEP_TONE.approved : STEP_TONE.inactive);
          return (
            <li key={i}
                data-testid={`chain-step-${i}`}
                className={`relative rounded-lg border p-3 ${tone}`}>
              <span className="absolute -left-6 top-3" data-testid={`chain-step-icon-${i}`}>
                <StepIcon status={step.status} isCurrent={isCurrent} />
              </span>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{step.label || `Step ${i + 1}`}</span>
                  {(step.mode === 'AND' || step.mode === 'OR') && (
                    <span data-testid={`chain-step-mode-${i}`}
                          className="text-xs px-1.5 py-0.5 rounded bg-white/60 border border-current font-mono">
                      {step.mode}
                    </span>
                  )}
                  {(step.roles || []).map((r) => (
                    <span key={r} data-testid={`chain-step-role-${i}-${r}`}
                          className="text-xs px-1.5 py-0.5 rounded bg-white/60 border border-current">
                      {r.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
                <span className="text-xs opacity-70 capitalize" data-testid={`chain-step-status-${i}`}>
                  {step.status}
                </span>
              </div>

              {(step.decisions || []).length > 0 && (
                <ul className="mt-2 space-y-1 text-xs">
                  {step.decisions.map((d, di) => (
                    <li key={di} data-testid={`chain-step-decision-${i}-${di}`}
                        className="flex items-start gap-2">
                      {d.decision === 'approve'
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        : <XCircle className="w-3.5 h-3.5 text-rose-600 mt-0.5 flex-shrink-0" />}
                      <div className="flex-1">
                        <span className="font-medium">{d.user_name || d.user_id}</span>
                        <span className="opacity-70"> · {d.role}</span>
                        {d.note && <span className="opacity-90"> — “{d.note}”</span>}
                        {d.snapshot_revision_hash && (
                          <span className="ml-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white border border-current font-mono opacity-80"
                                title={`Snapshot hash: ${d.snapshot_revision_hash}`}>
                            {String(d.snapshot_revision_hash).slice(0, 10)}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>

      {chain.status === 'in_progress' && canDecide && (
        <div className="border-t border-indigo-200 bg-indigo-50/50 px-3 py-3" data-testid="chain-decide-panel">
          <textarea
            data-testid="chain-decision-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note…"
            className="w-full border border-indigo-200 rounded p-2 text-sm focus:outline-none focus:border-indigo-400"
            rows={2}
          />
          <div className="flex gap-2 mt-2">
            <button
              data-testid="chain-approve-button"
              disabled={busy}
              onClick={() => submit('approve')}
              className="flex-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Approve Step {currentIdx + 1}
            </button>
            <button
              data-testid="chain-reject-button"
              disabled={busy}
              onClick={() => submit('reject')}
              className="flex-1 px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1">
              <XCircle className="w-4 h-4" /> Reject
            </button>
          </div>
        </div>
      )}

      {chain.status === 'in_progress' && !canDecide && (
        <div data-testid="chain-no-permission-panel"
             className="border-t border-indigo-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          Waiting for: <strong>{(steps[currentIdx]?.roles || []).join(' / ').replace(/_/g, ' ')}</strong>
          {isSubmitter && currentUserRole !== 'admin' && (
            <span className="ml-2 opacity-70">(submitter cannot self-approve)</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ApprovalChainTimeline;
